import { useCallback, useState } from 'react';

import { Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import type { SavedSearchFieldsFragment } from '__generated__/athena';

import type { RemoveSavedSearchResult } from './useSavedSearches';

export interface ConfirmDeleteDialogProps {
  savedSearch: SavedSearchFieldsFragment;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Deletes via the host's shared `useSavedSearches` instance so the dropdown's
   * list refetches and drops the row in place (a separate instance would only
   * reflect the removal on reload).
   */
  onConfirmDelete: (id: string) => Promise<RemoveSavedSearchResult>;
}

const GENERIC_ERROR = 'Failed to delete search. Please try again.';

export const ConfirmDeleteDialog = ({
  savedSearch,
  isOpen,
  onClose,
  onConfirmDelete,
}: ConfirmDeleteDialogProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    onClose();
  }, [submitting, onClose]);

  const handleConfirm = useCallback(async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await onConfirmDelete(savedSearch.id);
    setSubmitting(false);
    if (result.success) {
      onClose();
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_ERROR);
  }, [onConfirmDelete, savedSearch.id, submitting, onClose]);

  let confirmLabel = 'Delete';
  if (submitting) {
    confirmLabel = 'Deleting…';
  }

  return (
    <Dialog.Root
      role="dialog"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      disablePointerDismissal={submitting}
    >
      <Dialog title="Delete saved search?">
        <Dialog.Description className="text-body-md text-od-content-secondary">
          {`This will remove "${savedSearch.name}". You can’t undo this action.`}
        </Dialog.Description>
        {errorMessage && (
          <Text as="p" role="alert" className="pt-3 text-body-sm text-od-content-error">
            {errorMessage}
          </Text>
        )}
        <Row className="justify-end gap-2 pt-6">
          <Button
            variant="secondary"
            size="md"
            aria-label="Cancel"
            analyticsName="confirm-delete-saved-search-cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            aria-label={confirmLabel}
            analyticsName="confirm-delete-saved-search-confirm"
            onClick={handleConfirm}
            disabled={submitting}
            loading={submitting}
          >
            {confirmLabel}
          </Button>
        </Row>
      </Dialog>
    </Dialog.Root>
  );
};
